import { useEffect, useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Sparkles, ArrowRight } from "lucide-react";
import MomentBanner from "@/components/MomentBanner";

/**
 * Full-screen "Practice complete!" transition card.
 *
 * Fires exactly once per (learner, sub-topic), the moment Pool A is established
 * and Pool B becomes available. No score. No X/Y. Just the achievement.
 * Learner must tap "Let's go" — no auto-dismiss.
 */
export default function PoolACompleteScreen({ payload, onProceed }) {
  const subTopic = payload?.sub_topic || "this sub-topic";
  const headline = payload?.headline || "Practice complete!";
  const message =
    payload?.message ||
    `You've completed Practice for ${subTopic}. Challenge questions are now unlocked. Ready to go deeper?`;
  const moment = payload?.mitraa_moment;

  // Render a small burst of confetti / spark dots
  const [sparks] = useState(() =>
    Array.from({ length: 18 }).map((_, i) => ({
      id: i,
      left: Math.random() * 100,
      delay: Math.random() * 0.6,
      duration: 1.6 + Math.random() * 1.2,
      hue: [340, 45, 200, 280, 160][i % 5],
    }))
  );

  // Lock the body scroll while overlay is shown
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, []);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/70 backdrop-blur-sm px-4 py-8"
      data-testid="pool-a-complete-overlay"
    >
      {/* Confetti / spark layer */}
      <div className="pointer-events-none absolute inset-0 overflow-hidden">
        {sparks.map((s) => (
          <span
            key={s.id}
            className="absolute block w-2.5 h-2.5 rounded-sm spark-fall"
            style={{
              left: `${s.left}%`,
              top: "-10px",
              backgroundColor: `hsl(${s.hue} 90% 60%)`,
              animationDelay: `${s.delay}s`,
              animationDuration: `${s.duration}s`,
            }}
          />
        ))}
      </div>

      <Card
        className="relative z-10 rounded-3xl p-8 sm:p-10 max-w-lg w-full mit-shadow border-violet-200 bg-white text-center fade-up"
        data-testid="pool-a-complete-card"
      >
        {/* Gamification Moment Banner */}
        <MomentBanner moment={moment} className="mb-5" />
        
        <div
          className="text-xs uppercase tracking-[0.18em] font-semibold text-violet-500 mb-3"
          data-testid="pool-a-complete-subtopic"
        >
          {subTopic}
        </div>

        <div className="w-20 h-20 rounded-full bg-gradient-to-br from-violet-100 to-amber-100 flex items-center justify-center mx-auto mb-5 ring-1 ring-violet-200">
          <Sparkles className="w-10 h-10 text-violet-500" />
        </div>

        <h2
          className="font-display text-3xl sm:text-4xl font-bold text-slate-900 mb-3"
          data-testid="pool-a-complete-headline"
        >
          {headline}
        </h2>

        <p
          className="text-slate-700 text-base sm:text-lg leading-relaxed mb-7"
          data-testid="pool-a-complete-message"
        >
          {message}
        </p>

        <Button
          onClick={onProceed}
          className="rounded-full bg-violet-500 hover:bg-violet-600 text-white px-8 py-6 text-base font-semibold tap-44"
          data-testid="pool-a-complete-cta"
        >
          Let's go <ArrowRight className="w-5 h-5 ml-1.5" />
        </Button>
      </Card>

      <style>{`
        @keyframes spark-fall {
          0%   { transform: translateY(-20px) rotate(0deg); opacity: 0; }
          10%  { opacity: 1; }
          90%  { opacity: 1; }
          100% { transform: translateY(110vh) rotate(540deg); opacity: 0; }
        }
        .spark-fall {
          animation-name: spark-fall;
          animation-timing-function: cubic-bezier(.2,.6,.4,1);
          animation-iteration-count: 1;
          animation-fill-mode: forwards;
        }
      `}</style>
    </div>
  );
}
